import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IntegrationOauthService,
  IntegrationStatus,
  OauthProvider
} from './integration-oauth.service';
import { LoggerService } from '../services/logger.service';

/**
 * Integration Connection Chip
 * ----------------------------
 * Pill compacta para conectar/desconectar un provider OAuth (Jira, GitLab)
 * desde el ExportPanel sin salir del flujo. Tres estados visuales:
 *
 *   1. Loading   — punto neón pulsante, "Comprobando…"
 *   2. Connected — punto verde fijo, "CONECTADO COMO <accountName>" + ✕ desconectar
 *   3. Disconnected — punto rojo fijo, "CONECTAR <provider>" (click abre popup OAuth)
 *
 * Emite `(connectionChanged)` cada vez que el estado real cambia para que el
 * panel pueda re-cargar dependientes (p. ej. JiraOptions re-fetcha proyectos).
 *
 * @author Marcos Cordo
 * @version 07/06/2026_1
 */
@Component({
  selector: 'app-integration-connection-chip',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './integration-connection-chip.component.html',
  styleUrls: ['./integration-connection-chip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IntegrationConnectionChipComponent implements OnInit, OnChanges {
  @Input({ required: true }) provider!: OauthProvider;

  /** Emitido cuando el estado cambia (conectó / desconectó). */
  @Output() connectionChanged = new EventEmitter<IntegrationStatus>();

  loading = signal(true);
  status = signal<IntegrationStatus | null>(null);
  errorMessage = signal<string | null>(null);

  private oauth = inject(IntegrationOauthService);
  private logger = inject(LoggerService);

  ngOnInit(): void {
    void this.refreshStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['provider'] && !changes['provider'].firstChange) {
      void this.refreshStatus();
    }
  }

  async refreshStatus(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const s = await this.oauth.getStatus(this.provider);
      this.status.set(s);
    } catch (err: unknown) {
      this.logger.warn(`[IntegrationChip] getStatus(${this.provider}) falló:`, err);
      this.status.set({ connected: false });
      this.errorMessage.set('Error consultando estado.');
    } finally {
      this.loading.set(false);
    }
  }

  async onConnectClick(): Promise<void> {
    if (this.loading()) return;
    if (this.status()?.connected) return;          // ya conectado, no hacer nada
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const result = await this.oauth.connect(this.provider);
      if (result.ok) {
        await this.refreshStatus();
        this.connectionChanged.emit(this.status()!);
      } else {
        this.errorMessage.set(humanizeError(result.error));
        this.loading.set(false);
      }
    } catch (err: unknown) {
      this.logger.error(`[IntegrationChip] connect(${this.provider}) error:`, err);
      this.errorMessage.set('Error al iniciar conexión.');
      this.loading.set(false);
    }
  }

  async onDisconnectClick(event: Event): Promise<void> {
    event.stopPropagation();
    if (this.loading()) return;
    this.loading.set(true);
    try {
      await this.oauth.disconnect(this.provider);
      this.status.set({ connected: false });
      this.connectionChanged.emit({ connected: false });
    } catch (err: unknown) {
      this.logger.error(`[IntegrationChip] disconnect(${this.provider}) error:`, err);
      this.errorMessage.set('Error al desconectar.');
    } finally {
      this.loading.set(false);
    }
  }

  providerLabel(): string {
    return this.provider.charAt(0).toUpperCase() + this.provider.slice(1);
  }
}

function humanizeError(code?: string): string {
  switch (code) {
    case 'popup_blocked':         return 'Popup bloqueado. Permite popups del dominio.';
    case 'popup_closed_by_user':  return 'Cancelado.';
    case 'timeout':               return 'Tiempo de espera agotado.';
    default:                      return code ? `Error: ${code}` : 'Error desconocido.';
  }
}
